import { type CSSProperties, type FC, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import useMeasure from 'react-use-measure';
import StarIcon from '@assets/img/icons/star.svg?react';
import cn from 'classnames';
import { useOnClickOutside } from 'usehooks-ts';

import { type LeagueRewardType } from '@/common/types/league';
import {
  StringMain,
  StringSmall,
  StringXSmall,
} from '@/components/Typography/Typography';
import { Picture } from '@/components/ui/Picture/Picture';
import VipAvatar from '@/components/ui/VipAvatar/VipAvatar';

import { chestImages, cupImages } from '../config';
import { getChestKindByLeaguePosition } from '../lib';
import { RewardItem } from '../RewardItem/RewardItem';

import { getDecorationClass } from './decorations/registry';
import { PositionItemRewards } from './PositionItemRewards';

import css from './styles.module.scss';

interface PositionItemProps {
  // Строка — для случаев типа '50+' (юзер за пределами окна лидерборда).
  // Стили top-3 и сундуки реагируют только на численное значение.
  position: number | string;
  avatar: string;
  level: number;
  firstName?: string;
  lastName?: string;
  name?: string;
  starPoints?: number;
  score?: string;
  userName?: string | null;
  id?: string;
  user_id?: string;
  rewards?: LeagueRewardType['rewards'];
  isUserPosition?: boolean;
  className?: string;
  style?: CSSProperties;
  isVip?: boolean;
  decorationId?: string;
  // Если у строки есть призовой скин (bingo) — добавляем превью в popup
  // вместе с обычными rewards. Один общий popup, один общий чек-аут.
  prizeSkinId?: string;
}

const PositionItem: FC<PositionItemProps> = ({
  position,
  avatar,
  lastName,
  firstName,
  level,
  starPoints,
  rewards,
  isUserPosition,
  className,
  style,
  name,
  score,
  isVip,
  decorationId,
  prizeSkinId,
}) => {
  const { t } = useTranslation();
  const [refFullNameCell, { width }] = useMeasure();
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const chestRef = useRef<HTMLDivElement | null>(null);
  const [positionRewardIsOpen, setPositionRewardIsOpen] = useState(false);
  const [top, setTop] = useState(0);

  const setTopOffset = (): void => {
    setTop(wrapperRef.current?.offsetTop || 0);
  };

  // Закрываем popup при клике/тапе вне честа — то же поведение, что и в
  // `PositionItemRewards` (арена), чтобы плашка реагировала одинаково.
  useOnClickOutside(chestRef, () => setPositionRewardIsOpen(false));
  useOnClickOutside(
    chestRef,
    () => setPositionRewardIsOpen(false),
    'touchstart',
  );

  const decorationClass = getDecorationClass(decorationId);
  const hasDecoration = !!decorationClass;

  const numericPosition = typeof position === 'number' ? position : undefined;
  const kind =
    numericPosition === 1
      ? 'gold'
      : numericPosition === 2
        ? 'silver'
        : numericPosition === 3
          ? 'bronze'
          : 'default';

  return (
    <div
      ref={wrapperRef}
      style={style}
      className={cn(
        'w-full p-1 h-[3.125rem] rounded-lg relative',
        hasDecoration ? decorationClass : 'bg-white/10 backdrop-blur-3xl',
        className,
        css.positionItem,
      )}
    >
      <div className="flex items-center justify-center mr-1">
        {kind === 'default' || name ? (
          <StringMain
            weight="bold"
            className={cn(
              '!text-[16px]',
              css.position,
              css[`position--${kind}`],
            )}
          >
            {position}
          </StringMain>
        ) : (
          <Picture
            className="w-full h-full"
            img={cupImages[kind].png}
            webp={cupImages[kind].webp}
            alt={kind}
          />
        )}
      </div>
      <div className="w-9 h-9 relative mr-2">
        <div
          style={{ backgroundImage: `url("${avatar}")` }}
          className="z-[1] absolute left-0 top-0 w-full h-full rounded-full bg-center bg-cover overflow-hidden"
        ></div>
        {isVip && <VipAvatar />}
      </div>
      <div
        ref={refFullNameCell}
        className="flex flex-col justify-between items-start gap-[4px] mr-2"
      >
        <span
          style={{ maxWidth: width - 15 }}
          className="!leading-none !text-white text-sm crop-long-text"
        >
          {name ? name : `${firstName} ${lastName}`}
        </span>
        {score ? (
          <div className={cn(css.level, css[`level--${kind}`])}>
            <span className="font-medium text-xs leading-none text-white ">
              {`${t('lvl')} ${level}`}
            </span>
          </div>
        ) : (
          <div className="flex gap-1 items-center justify-start">
            <StarIcon className="size-5 shrink-0 fill-gold" />
            <StringSmall weight="bold">{starPoints}</StringSmall>
          </div>
        )}
      </div>
      {!score ? (
        <PositionItemRewards
          rewards={rewards}
          kind={kind}
          setPositionRewardIsOpen={setPositionRewardIsOpen}
          setTopOffset={setTopOffset}
        />
      ) : (
        <div className="flex items-center gap-1 min-w-[6.25rem] absolute right-0">
          <img src="/img/leaderBoard/badge-small.svg" alt="" width={24} />
          <StringXSmall weight="bold">{score}</StringXSmall>
          {(rewards || prizeSkinId) && (
            <div
              ref={chestRef}
              onClick={(): void => {
                setTopOffset();
                setPositionRewardIsOpen((s) => !s);
              }}
              className="relative flex items-center justify-center ml-1 cursor-pointer"
            >
              <Picture
                className="size-10"
                img={
                  chestImages[
                    getChestKindByLeaguePosition(numericPosition) ?? 'default'
                  ].png
                }
                webp={
                  chestImages[
                    getChestKindByLeaguePosition(numericPosition) ?? 'default'
                  ].webp
                }
                alt="prize"
              />
            </div>
          )}
        </div>
      )}
      {!hasDecoration && (
        <div className="absolute top-0 left-0 overflow-hidden w-full h-full rounded-lg -z-[1]">
          <div
            className={cn(
              css.elipse,
              css[`elipse--${isUserPosition ? 'me' : kind}`],
            )}
          ></div>
        </div>
      )}
      {!hasDecoration && isVip && kind === 'default' && (
        <div className="absolute top-0 right-0 overflow-hidden w-full h-full rounded-lg -z-[1]">
          <div className={cn(css.elipse, css['elipse--vip'])}></div>
        </div>
      )}
      {positionRewardIsOpen &&
        (rewards || prizeSkinId) &&
        !name &&
        document.getElementById('league-rank') &&
        createPortal(
          <div
            style={{
              top: top - 64,
              right: 0,
            }}
            className={css.infoDetail}
          >
            {prizeSkinId && (
              <div className="flex flex-col items-center gap-[2px]">
                <div
                  className={cn(
                    css.bingoPrizeDecoration,
                    getDecorationClass(prizeSkinId),
                  )}
                />
                <span className="text-[12px] leading-none font-bold text-white">
                  {t('Skin')}
                </span>
              </div>
            )}
            {rewards &&
              [
                ...Object.entries(rewards.wallet),
                ...Object.entries(rewards.lottery || rewards.lotteries || {}),
                ...Object.entries(rewards.materials),
              ].map(([item, quantity]) => (
                <RewardItem
                  key={`reward-${item}`}
                  item={item}
                  quantity={quantity}
                />
              ))}

            <span className={css.polygon}></span>
          </div>,
          document.getElementById('league-rank') as HTMLElement,
        )}
    </div>
  );
};

export { PositionItem };
